<template>
  <div class="document-header">
    <div class="details-container">
      <div v-if="videoThumbnail" class="header-video-thumbnail">
        <VideoPlayer
          :url="videoUrl"
          :thumb-url="videoThumbnail"
          :controls="false"
          :play-on-hover="true"
          :muted="true"
          @video-clicked="handleClick"
        />
        <PlayIcon />
      </div>
      <div class="expires-container">
        <FriedH3 class="header-document-name">{{ name }}</FriedH3>
        <FriedParagraph
          v-if="!!expirationDate && showExpiration"
          :class="['expiration-text', { warning: expirationWarning, 'signed-text': isSigned }]"
        >
          {{ documentStatusInformationText }}
        </FriedParagraph>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import VideoPlayer from '@getaccept/lib-shared/src/video/player/components/VideoPlayer.vue';
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import PlayIcon from '../assets/play.svg';

export default defineComponent({
  components: {
    VideoPlayer,
    PlayIcon,
  },
  props: {
    name: { type: String, default: '' },
    expirationDate: { type: String, default: '' },
    videoThumbnail: { type: String, default: '' },
    videoUrl: { type: String, default: '' },
    reminderExpirationDays: { type: Number, default: undefined },
    isSigned: { type: Boolean },
    signDate: { type: String, default: '' },
    showExpiration: { type: Boolean, default: true },
  },
  emits: ['videoIntro'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const time = ref(DateTime.local({ zone: 'utc' }));
    const intervalTimer = ref(null);

    const watchIsSigned = () => {
      time.value = DateTime.local({ zone: 'utc' });
    };
    watch(() => props.isSigned, watchIsSigned);

    const documentStatusInformationText = computed((): string =>
      props.isSigned ? signedText.value : expirationText.value
    );

    const signedText = computed((): string => {
      const timeAgo = DateTime.fromISO(props.signDate, { zone: 'utc' }).toRelative({
        base: time.value,
        padding: 1000,
      });
      return `${t('signed')} ${timeAgo}`;
    });

    const expirationText = computed((): string => {
      const expiration = DateTime.fromISO(props.expirationDate, { zone: 'utc' });
      const timeTo: string = expiration.toRelative({
        base: time.value,
        padding: 1000,
      });
      const expired: boolean = expiration < time.value;
      return expired ? `${t('expired')} ${timeTo}` : `${t('expires')} ${timeTo}`;
    });

    const expirationWarning = computed((): boolean => {
      if (props.isSigned) {
        return false;
      }
      const diff = DateTime.fromISO(props.expirationDate, { zone: 'utc' })
        .diff(time.value, 'days')
        .toObject().days;

      return diff < props.reminderExpirationDays;
    });

    const handleClick = () => {
      emit('videoIntro');
    };

    onMounted(() => {
      intervalTimer.value = setInterval(() => {
        time.value = DateTime.local({ zone: 'utc' });
      }, 60000);
    });

    onBeforeUnmount(() => {
      clearInterval(intervalTimer.value);
    });

    return {
      time,
      intervalTimer,
      documentStatusInformationText,
      signedText,
      expirationText,
      expirationWarning,
      handleClick,
    };
  },
});
</script>

<style lang="scss" scoped>
$video-width: 95px;

.document-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  max-width: var(--page-width);
  background-color: var(--white);
  padding: var(--spacing-100);
  margin-top: var(--spacing-50);
  box-sizing: border-box;
  width: 100%;
  height: 5rem;
  box-shadow: var(--shadow-1);
  border-radius: var(--radius-medium);

  @media screen and (max-width: $sm) {
    height: fit-content;
  }

  @media screen and (min-width: $xs) {
    margin-top: var(--spacing-100);
  }

  @media screen and (min-width: $md) {
    margin-top: var(--spacing-150);
  }

  .details-container {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: inherit;

    @media screen and (max-width: $xxs) {
      flex-direction: column;
      gap: var(--spacing-100);
    }

    .header-video-thumbnail {
      margin-right: var(--spacing-150);
      overflow: hidden;
      position: relative;
      width: $video-width;
      height: calc(5rem - #{var(--spacing-100)} * 2);
      border-radius: 1px;
      cursor: pointer;

      @media screen and (max-width: $xxs) {
        width: 100%;
        margin: 0;
        height: auto;
        flex-shrink: 0;
      }

      svg {
        height: 2rem;
        width: 2rem;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        pointer-events: none;
        transition: opacity 0.3s ease-in-out;
      }

      &:hover svg {
        opacity: 0;
      }
    }

    .expires-container {
      font-weight: 500;
      color: var(--text-gray);
      width: inherit;

      .header-document-name {
        margin: 0;
        margin-bottom: var(--spacing-25);
        white-space: pre-wrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .expiration-text {
        margin: 0;
        color: var(--text-gray);

        &.warning {
          color: var(--error-dark);
        }
      }

      .signed-text {
        margin: 0;
        color: var(--success-dark);
      }
    }
  }
}
</style>
